<script setup lang="ts">
import { type StyleValue } from 'vue';
import { createPopper, type Instance as PopperInstance } from '@popperjs/core';
import NavIcon from './NavIcon.vue';
import { type NavbarIcon, type NavPosition } from './types';
import { NuxtLink } from '#components';

const {
    handleOverflow,
    to,
    top = '56px',
} = defineProps<{
    position: NavPosition;
    title: string;
    to?: string;
    href?: string;
    icon?: NavbarIcon;
    iconDefaultColor?: string;
    handleOverflow?: boolean;
    top?: string;
    loading?: boolean;
}>();

defineOptions({
    inheritAttrs: false,
});

const slots = useSlots();
const attrs = useAttrs();
const hasSubItems = computed(() => !!slots.default);
const hasAction = computed(() => !!attrs.onClick);
const expanded = ref(false);
const maxHeight = ref(0);
const subItemsContainer = ref<HTMLDivElement>();
const itemClicked = (ev: MouseEvent) => {
    if (hasSubItems.value) {
        expanded.value = !expanded.value;
    } else if (hasAction.value) {
        (attrs as any).onClick?.(ev);
    }
};
watch(expanded, value => {
    if (value) {
        maxHeight.value = subItemsContainer.value?.clientHeight || 0;
    }
});

// popper
const subItems = ref<HTMLDivElement>();
const itemButton = ref<HTMLDivElement>();
const popper = ref<PopperInstance>();
onMounted(() => {
    if (hasSubItems.value && handleOverflow) {
        popper.value = createPopper(itemButton.value!, subItems.value!, {
            placement: 'bottom-end',
        });
    }
});
onBeforeUnmount(() => popper.value?.destroy());
</script>

<template>
    <li
        class="item"
        :class="[position, attrs.class, { loading }]"
        :style="attrs.style as StyleValue"
    >
        <NuxtLink v-if="to" :to class="body">
            <NavIcon
                v-if="icon"
                :icon
                :default-color="iconDefaultColor"
                class="icon"
            />
            <span class="title">{{ title }}</span>
        </NuxtLink>
        <a v-else-if="href" :href class="body">
            <NavIcon
                v-if="icon"
                :icon
                :default-color="iconDefaultColor"
                class="icon"
            />
            <span class="title">{{ title }}</span>
        </a>
        <div
            v-else-if="hasAction || hasSubItems || loading"
            ref="itemButton"
            class="body"
            @click="itemClicked"
        >
            <NavIcon
                v-if="icon"
                :icon
                :default-color="iconDefaultColor"
                class="icon"
            />
            <span class="title">{{ title }}</span>
            <slot v-if="hasSubItems" name="expand" :expanded />
        </div>
        <div
            v-if="hasSubItems"
            ref="subItems"
            class="sub-items"
            :class="{ hidden: !expanded }"
            :style="{ '--max-height': `${maxHeight}px` }"
        >
            <ul ref="subItemsContainer" class="sub-menu">
                <slot />
            </ul>
        </div>
    </li>
</template>

<style scoped lang="scss">
.loading {
    border-color: usePalette($p-blue, $p-100) !important;
    border-radius: 20px;
    background-image: linear-gradient(
        90deg,
        rgba(61, 70, 79, 0.1) 0%,
        rgba(61, 70, 79, 0.25) 100%
    ) !important;
    background-size: 200% 100% !important;
    animation: 1.5s shine-skeleton linear infinite !important;
    color: transparent !important;
    user-select: none !important;
    cursor: unset !important;
    * {
        color: transparent !important;
    }
}
.sidebar.item {
    width: 100%;
    cursor: pointer;
    .selected {
        .title {
            color: usePalette($p-blue, 400) !important;
        }
        :deep(.mr-icon-svg) {
            color: usePalette($p-blue, 400) !important;
        }
    }
    .body {
        display: flex;
        align-items: center;
        padding-block: 6px;
        &:hover {
            text-decoration: none;
        }
        &.router-link-exact-active {
            .title {
                color: usePalette($p-blue, 400) !important;
            }
            :deep(.mr-icon-svg) {
                color: usePalette($p-blue, 400) !important;
            }
        }
        .icon {
            margin-left: 0;
        }
        .title {
            @include tg-m($w5);
            margin-right: 12px;
            flex-grow: 1;
        }
    }
    .sub-items {
        overflow: hidden;
        max-height: var(--max-height);
        background-color: usePalette($p-cool-gray, $p-100);
        border-radius: 8px;
        transition: linear 0.2s;
        &.hidden {
            max-height: 0;
        }
        .sub-menu {
            list-style-type: none;
            padding: 16px 12px;
            display: flex;
            flex-direction: column;
            gap: 12px;
        }
    }
}

.header.item {
    width: auto;
    border: 0 !important;
    white-space: nowrap;
    &:last-child {
        margin-left: 0;
    }
    .body {
        display: flex;
        align-items: center;
        border-radius: 20px;
        width: fit-content;
        padding: 8px;
        color: var(--color) !important;
        font-size: 16px !important;
        text-decoration: none;
        &:hover {
            background-color: rgba($color: black, $alpha: 0.1);
            & + .sub-items {
                visibility: visible;
                opacity: 1;
                -webkit-transition-delay: 0s;
                -moz-transition-delay: 0s;
                -ms-transition-delay: 0s;
                -o-transition-delay: 0s;
                transition-delay: 0s;
            }
        }
        .icon {
            margin: 0;
            &:first-child {
                margin-left: 8px;
            }
            &:not(:first-child) {
                margin-right: 8px;
            }
        }
    }
    .sub-items {
        visibility: hidden;
        opacity: 0;
        -webkit-transition:
            visibility 0s linear 0.2s,
            opacity 0.2s linear;
        -moz-transition:
            visibility 0s linear 0.2s,
            opacity 0.2s linear;
        -ms-transition:
            visibility 0s linear 0.2s,
            opacity 0.2s linear;
        -o-transition:
            visibility 0s linear 0.2s,
            opacity 0.2s linear;
        transition:
            visibility 0s linear 0.2s,
            opacity 0.2s linear;
        &:hover {
            visibility: visible;
            opacity: 1;
            -webkit-transition-delay: 0s;
            -moz-transition-delay: 0s;
            -ms-transition-delay: 0s;
            -o-transition-delay: 0s;
            transition-delay: 0s;
        }
        max-width: 230px;
        width: auto;
        min-width: 230px;
        position: absolute;
        border-radius: 8px;
        padding-inline: 0;
        top: v-bind(top);
        z-index: 1000000;
        background-color: white;
        @include shadow(2);
        .sub-menu {
            padding: 0;
            border-radius: 8px;
            list-style-type: none;
        }
    }
}
</style>
